// hooks/useCustomerData.js - IndexedDB 전용 고객 데이터 관리
import { useEffect, useState, useCallback } from 'react';
import {
    openDb,
    idbGetCustomersByStage,
    idbPutCustomer,
    idbDeleteCustomer,
    idbSearchCustomers,
    migrateAllLocalStorageData
} from '../utils/idb';
import { addCustomerHistory } from './useCustomerHistory';


export const useCustomerData = () => {
    // 상태 관리
    const [customers, setCustomers] = useState({});
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // IndexedDB에서 고객 데이터 로드
    const loadCustomersFromIdb = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            await openDb();

            // 1단계: 마이그레이션 실행 (필요한 경우)
            const migrationResult = await migrateAllLocalStorageData();
            console.log('🔄 마이그레이션 결과:', migrationResult);

            // 2단계: 모든 단계의 고객 데이터 로드
            const stages = ['prospect', 'preparation', 'appointment', 'consultation', 'closed', 'rejected', 'bank'];
            const customersData = {};

            for (const stage of stages) {
                const rows = await idbGetCustomersByStage(stage, 0, 100);
                const sorted = (rows || []).slice().sort((a, b) => {
                    const ad = new Date(a.createdAt || 0).getTime();
                    const bd = new Date(b.createdAt || 0).getTime();
                    return bd - ad; // 최신 등록 우선
                });
                customersData[stage] = sorted;
            }

            // 3단계: 과거 고객 필드의 Follow Up 값을 히스토리로 이관 (마이그레이션)
            // 재생성 방지: 기기 단위 1회만 수행
            const followUpMigratedFlag = localStorage.getItem('migratedFollowUpV1') === 'true';
            try {
                if (!followUpMigratedFlag) {
                    for (const stage of Object.keys(customersData)) {
                        const migratedStageRows = [];
                        for (const c of customersData[stage] || []) {
                            const legacy = c.followUpDate || c.followUpPlanDate || c.followUp || null;
                            if (legacy) {
                                let dateStr = '';
                                if (typeof legacy === 'string') {
                                    dateStr = legacy; // YYYY-MM-DD or ISO
                                } else if (legacy instanceof Date) {
                                    const y = legacy.getFullYear();
                                    const m = String(legacy.getMonth() + 1).padStart(2, '0');
                                    const d = String(legacy.getDate()).padStart(2, '0');
                                    dateStr = `${y}-${m}-${d}`;
                                }

                                try {
                                    await addCustomerHistory(c.id, {
                                        type: 'followup',
                                        note: '과거 고객 필드 Follow Up 이관',
                                        followUp: { content: null, date: dateStr }
                                    });
                                } catch (_) { /* noop */ }

                                // 고객 객체의 레거시 필드는 제거
                                const cleaned = { ...c };
                                delete cleaned.followUpDate;
                                delete cleaned.followUpPlanDate;
                                delete cleaned.followUp;
                                await idbPutCustomer(cleaned);
                                migratedStageRows.push(cleaned);
                            } else {
                                migratedStageRows.push(c);
                            }
                        }
                        customersData[stage] = migratedStageRows;
                    }
                    localStorage.setItem('migratedFollowUpV1', 'true');
                }
            } catch (e) {
                console.warn('Follow Up 마이그레이션 경고:', e);
            }

            // 4단계: 데이터 상태 확인
            const hasData = Object.values(customersData).some(stageCustomers => stageCustomers.length > 0);

            console.log('📊 데이터 존재 여부 확인:', {
                hasIdbData: hasData,
                totalCustomers: Object.values(customersData).flat().length
            });

            setCustomers(customersData);
        } catch (err) {
            console.error('고객 데이터 로드 실패:', err);
            setError(err.message);
            // 폴백: 빈 데이터 사용
            setCustomers({});
        } finally {
            setLoading(false);
        }
    }, []);

    // 고객 추가
    const addCustomer = useCallback(async (customerData) => {
        try {
            const newCustomer = {
                ...customerData,
                id: customerData.id || crypto.randomUUID(),
                createdAt: customerData.createdAt || new Date().toISOString()
            };

            await idbPutCustomer(newCustomer);

            // 상태 업데이트
            setCustomers(prev => {
                const nextStageList = [...(prev[newCustomer.stage] || [])];
                // 맨 앞에 추가 후 정렬 유지
                const merged = [newCustomer, ...nextStageList].sort((a, b) => {
                    const ad = new Date(a.createdAt || 0).getTime();
                    const bd = new Date(b.createdAt || 0).getTime();
                    return bd - ad;
                });
                return {
                    ...prev,
                    [newCustomer.stage]: merged
                };
            });

            return newCustomer;
        } catch (err) {
            console.error('고객 추가 실패:', err);
            throw err;
        }
    }, []);

    // 고객 수정
    const updateCustomer = useCallback(async (customerId, updates) => {
        try {
            const updatedCustomer = { ...updates, id: customerId };
            await idbPutCustomer(updatedCustomer);

            // 상태 업데이트
            setCustomers(prev => {
                const newCustomers = { ...prev };
                Object.keys(newCustomers).forEach(stage => {
                    newCustomers[stage] = newCustomers[stage].map(customer =>
                        customer.id === customerId ? updatedCustomer : customer
                    );
                });
                return newCustomers;
            });

            return updatedCustomer;
        } catch (err) {
            console.error('고객 수정 실패:', err);
            throw err;
        }
    }, []);

    // 고객 삭제
    const deleteCustomer = useCallback(async (customerId) => {
        try {
            await idbDeleteCustomer(customerId);

            // 상태 업데이트
            setCustomers(prev => {
                const newCustomers = { ...prev };
                Object.keys(newCustomers).forEach(stage => {
                    newCustomers[stage] = newCustomers[stage].filter(customer => customer.id !== customerId);
                });
                return newCustomers;
            });

            return true;
        } catch (err) {
            console.error('고객 삭제 실패:', err);
            throw err;
        }
    }, []);

    // 고객 검색
    const searchCustomers = useCallback(async (query) => {
        try {
            return await idbSearchCustomers(query);
        } catch (err) {
            console.error('고객 검색 실패:', err);
            return [];
        }
    }, []);

    // 초기 로드
    useEffect(() => {
        loadCustomersFromIdb();
    }, [loadCustomersFromIdb]);

    return {
        customers,
        setCustomers,
        currentStep,
        setCurrentStep,
        loading,
        error,
        addCustomer,
        updateCustomer,
        deleteCustomer,
        searchCustomers,
        reload: loadCustomersFromIdb
    };
};